import type { Answers, RecapSegment, TSuppressionProfile } from "./_schema";
import { deriveBoostProfile, type ExtraSignals } from "./boost-profile";

/**
 * Beer-quiz deriver. Wraps `deriveBoostProfile` (the cortisol-quiz pattern)
 * with three additions:
 *
 *   1. Answer translation: `age`, `energy-3pm` and `when-started` are
 *      ignite-verbatim passthroughs; two beer answers have a genuine quiz-v6
 *      construct match and are translated so they feed severity AND root causes
 *      ORGANICALLY (see the maps below).
 *   2. Sign severity: the five beer-only sign questions feed severity via the
 *      `extraSignals` seam (worst answer -> tier1, next -> tier2/tier3, the
 *      quiz-v7 cohort-gradient convention).
 *   3. Forced primary: `estrogen-dominance` leads the verdict on every path via
 *      `forcePrimaryCause`. The organic top scorer stays visible as Secondary.
 *
 * Why a NEW root cause rather than reusing an existing estrogen-adjacent one:
 * `visceral-adiposity` (AROMATASE LOAD) blames body fat, so it would contradict
 * the man who just answered "No gut here" two screens earlier; and
 * `blocked-testosterone` leads on the SHBG handcuff, which is the SECOND half of
 * Bridge 2, not the villain the ads and both bridges name. `estrogen-dominance`
 * names the swap itself, which is true at every reported beer volume.
 *
 * No `qolFloor`: unlike quiz-sleep, this quiz supplies `energy-3pm`,
 * `stress-symptoms` and `body-composition` to the base deriver, which is enough
 * for `deriveQolImpact` to reach `high` organically (two tier-1 domains).
 * Adding a floor here would be a severity dial, not a better signal.
 *
 * Pure function of `answers` -- reproducible server-side from the persisted
 * submission (rule 11).
 */

/** Ids whose values are ignite-verbatim -- copied through untranslated. */
const PASSTHROUGH_IDS = ["age", "energy-3pm", "when-started"] as const;

/**
 * `gut-stubborn` -> quiz-v6 `body-composition`. Translation, not manufacture: the
 * question asks the user to describe his own midsection against everything he
 * has cut, which is a body-composition self-report in the ad's language. This is
 * what makes `visceral-adiposity` scoreable as the organic Secondary -- the
 * on-angle pairing, since aromatase in belly fat is the second half of the same
 * estrogen story the forced primary opens.
 */
const GUT_TO_BODY: Record<
  string,
  "overweight" | "soft-some-muscle" | "average" | "lean-muscular"
> = {
  "wont-budge": "overweight",
  "shrinks-comes-back": "soft-some-muscle",
  "starting-creep": "average",
  "no-gut": "lean-muscular",
};

/**
 * `patience` -> quiz-v6 `stress-symptoms`. Genuine construct match: the quiz-v6
 * stress question is literally "tired, cranky, foggy, or moody", and losing
 * patience with the kids, the wife and the co-worker is the same crankiness in
 * the ad's language.
 */
const PATIENCE_TO_STRESS: Record<string, "yes" | "no"> = {
  "every-day": "yes",
  "most-days": "yes",
  "now-and-then": "no",
  "cool-as-ever": "no",
};

/**
 * Sign-question severity mapping. `gut-stubborn` and `patience` are absent by
 * design -- they already feed severity organically through the translations
 * above, and a signal must never be counted twice (the quiz-sleep note).
 *
 * `libido` and `softness` sit at tier1 as two DISTINCT constructs: wanting less
 * versus the body changing shape. `softness` earns tier1 rather than tier2
 * because its option names below-the-belt change alongside chest and gut, which
 * is a tier-1 correlate in docs/t-suppression-research.md, not a body-comp note.
 *
 * `beer-intake` is dose, not symptom, so it sits with quiz-v6's other lifestyle
 * multipliers. It is the only sign spanning tier2 and tier3: a cold one most
 * nights is a daily exposure to the strongest phytoestrogen in the food supply
 * (heavier than quiz-v6's processed-foods proxy, hence tier2), while weekend
 * clustering is real but intermittent (tier3).
 */
const TIER1_SIGNS: Record<string, readonly string[]> = {
  libido: ["barely-happening"],
  softness: ["noticeably"],
};
const TIER2_SIGNS: Record<string, readonly string[]> = {
  libido: ["body-wont-cooperate"],
  softness: ["starting-to"],
  strength: ["big-drop"],
  "bounce-back": ["next-day-derailed"],
  "beer-intake": ["most-nights"],
};
const TIER3_SIGNS: Record<string, readonly string[]> = {
  libido: ["less-often"],
  strength: ["noticeable"],
  "bounce-back": ["next-morning"],
  "beer-intake": ["weekends"],
};

function countTier(answers: Answers, map: Record<string, readonly string[]>): number {
  let n = 0;
  for (const [id, values] of Object.entries(map)) {
    const v = answers[id];
    if (typeof v === "string" && values.includes(v)) n += 1;
  }
  return n;
}

function signSignals(answers: Answers): ExtraSignals {
  return {
    tier1: countTier(answers, TIER1_SIGNS),
    tier2: countTier(answers, TIER2_SIGNS),
    tier3: countTier(answers, TIER3_SIGNS),
  };
}

/**
 * Builds the ignite-namespace answer set the base deriver reads: three
 * passthrough ids plus the two genuine construct translations. Nothing is mapped
 * to `concern` or `results-want` -- this quiz has no aspiration screen, and
 * mapping `libido` to `concern === "sex-drive"` would double-count it against
 * the tier1 sign above (the trap called out in quiz-sleep-profile.ts).
 */
function syntheticAnswers(answers: Answers): Answers {
  const out: Answers = {};
  for (const id of PASSTHROUGH_IDS) {
    const v = answers[id];
    if (typeof v === "string") out[id] = v;
  }
  const gut = answers["gut-stubborn"];
  if (typeof gut === "string" && gut in GUT_TO_BODY) {
    out["body-composition"] = GUT_TO_BODY[gut]!;
  }
  const patience = answers["patience"];
  if (typeof patience === "string" && patience in PATIENCE_TO_STRESS) {
    out["stress-symptoms"] = PATIENCE_TO_STRESS[patience]!;
  }
  return out;
}

// -- Bridge 2 body -------------------------------------------------------
//
// Lives here rather than inline in quiz-beer.ts so the static `body` and the
// `deriveBody` personalization below cannot drift apart: both read this array.
// quiz-beer.ts already imports from this module, so no dependency cycle.

/** Index of the paragraph `buildMechanismBody` personalizes. */
const ECHO_PARAGRAPH = 1;

export const MECHANISM_PARAGRAPHS = [
  "Testosterone is what makes you you. It’s the hormone behind your muscle, your energy, your drive, your edge.",
  "Every sign you just checked off is what it looks like when it drops.",
  "The beer isn’t starting the fire. It’s tossing lighter fluid on one that’s already burning.",
  "It gets worse. Most of the testosterone you still have is handcuffed to a protein your liver makes. So it’s not just that you’re making less. You can barely use what you’ve got.",
  "You don’t have to give up beer. You have to fight back on three fronts:",
  "Let’s see where your T stands.",
] as const;

/**
 * The signs this screen can read back to the user, in the order they read
 * naturally in a sentence. Only the answers that actually report a problem
 * appear -- echoing "no gut here" back at him would be absurd, and echoing a
 * middling answer would overstate what he said.
 *
 * Deliberately a subset: `patience` and `softness` are omitted because their
 * natural phrasings ("the short fuse", "the softness") sit oddly in a list
 * beside concrete ones, and three echoes is already the readable ceiling.
 */
const ECHOES: ReadonlyArray<readonly [questionId: string, value: string, phrase: string]> = [
  ["gut-stubborn", "wont-budge", "the gut that won’t budge"],
  ["gut-stubborn", "shrinks-comes-back", "the gut that keeps coming back"],
  ["energy-3pm", "drained", "the 3pm crash"],
  ["energy-3pm", "afternoon-slump", "the afternoon slump"],
  ["strength", "big-drop", "the strength that isn’t coming back"],
  ["libido", "barely-happening", "the bedroom"],
  ["libido", "body-wont-cooperate", "the bedroom"],
];

/** At most this many echoes; beyond three the sentence stops scanning. */
const MAX_ECHOES = 3;

function echoesFor(answers: Answers): string[] {
  const out: string[] = [];
  const usedQuestions = new Set<string>();
  for (const [questionId, value, phrase] of ECHOES) {
    if (out.length >= MAX_ECHOES) break;
    // One echo per question: `gut-stubborn` and `libido` each list two answer
    // values, and both must never contribute twice.
    if (usedQuestions.has(questionId)) continue;
    if (answers[questionId] === value) {
      out.push(phrase);
      usedQuestions.add(questionId);
    }
  }
  return out;
}

/** "a, b and c" -- serial comma omitted to match the house voice. */
function joinPhrases(phrases: readonly string[]): Array<{ text: string; echo: boolean }> {
  const parts: Array<{ text: string; echo: boolean }> = [];
  phrases.forEach((phrase, i) => {
    if (i > 0) parts.push({ text: i === phrases.length - 1 ? " and " : ", ", echo: false });
    parts.push({ text: phrase, echo: true });
  });
  return parts;
}

/**
 * Bridge 2's body, with the "every sign you just checked off" line rewritten to
 * name the signs he actually reported. The named runs are flagged
 * `customerProvided`, which the widget renders in mars-green -- so he can see
 * which words came from him.
 *
 * Returns `[]` when fewer than two signs were reported, which makes the widget
 * fall back to the static `body` (MECHANISM_PARAGRAPHS) verbatim. One echo
 * reads worse than none ("Every sign you just checked off -- the 3pm crash --"
 * is a list of one), and zero would leave a dangling dash.
 *
 * Pure function of `answers`, same contract as the deriver above (rule 2).
 */
export function buildMechanismBody(answers: Answers): readonly (readonly RecapSegment[])[] {
  const echoes = echoesFor(answers);
  if (echoes.length < 2) return [];

  const personalized: RecapSegment[] = [
    { text: "Every sign you just checked off — ", customerProvided: false },
    ...joinPhrases(echoes).map((p) => ({ text: p.text, customerProvided: p.echo })),
    { text: " — is what it looks like when it drops.", customerProvided: false },
  ];

  return MECHANISM_PARAGRAPHS.map((text, i) =>
    i === ECHO_PARAGRAPH ? personalized : [{ text, customerProvided: false }],
  );
}

export function deriveBeerProfile(answers: Answers): TSuppressionProfile {
  const profile = deriveBoostProfile(syntheticAnswers(answers), {
    extraSignals: signSignals(answers),
    forcePrimaryCause: "estrogen-dominance",
  });
  // Drives the beer-specific eyebrow / subline / projection subhead / offer
  // headline via COHORT_OVERLAYS (quizzes/quiz-v7.ts).
  return { ...profile, cohort: "beer" };
}
